import { useState, useEffect, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { 
  Save,
  ExternalLink,
  Edit,
  Eye,
  EyeOff,
  Lock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Loader2,
} from "lucide-react";
import { getItem, updateItem, getGuides } from "../../../services/api";
import type { UpdateItemRequest, Guide } from "../../../types";
import { toast } from "react-hot-toast";
import { getGuidePublicUrl } from "@/lib/utils";
import { BASE_PATH } from "@/config/basePath";

interface EditGuideItemModalProps {
  isOpen: boolean;
  onClose: () => void;
  helpCenterId: string;
  itemId: string;
  onSuccess?: () => void;
}

export function EditGuideItemModal({
  isOpen,
  onClose,
  helpCenterId,
  itemId,
  onSuccess,
}: EditGuideItemModalProps) {
  const [item, setItem] = useState<any | null>(null);
  const [guide, setGuide] = useState<Guide | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isPublished, setIsPublished] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const itemResponse = await getItem(helpCenterId, itemId);
      if (itemResponse.ok && itemResponse.item) {
        const itemData = itemResponse.item;
        
        if (itemData.item_type !== 'guide') {
          setError('This item is not a guide');
          return;
        }

        setItem(itemData);
        setIsPublished(itemData.is_published);

        // If guide data is already included in the item response, use it
        if (itemData.guide) {
          setGuide(itemData.guide as Guide);
        } else {
          // Otherwise, fetch guide details if we have guide ID
          const guideId = itemData.item_id;
          if (guideId) {
            const guidesResponse = await getGuides();
            if (guidesResponse.ok && guidesResponse.guides) {
              // Try to find guide by ID (handle both numeric and string IDs)
              const foundGuide = guidesResponse.guides.find((g: Guide) => {
                return g.id === guideId.toString() || 
                       g.id === String(guideId) ||
                       parseInt(g.id, 10) === parseInt(String(guideId), 10);
              });
              if (foundGuide) {
                setGuide(foundGuide);
              } else {
                console.warn('Guide not found in guides list:', guideId);
              }
            }
          }
        }
      } else {
        setError(itemResponse.error || 'Failed to fetch item');
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  }, [helpCenterId, itemId]);

  useEffect(() => {
    if (isOpen && helpCenterId && itemId) {
      fetchData();
    } else {
      // Reset state when modal closes
      setItem(null);
      setGuide(null);
      setError(null);
      setIsPublished(false);
    }
  }, [isOpen, helpCenterId, itemId, fetchData]);

  const handleSave = async () => {
    if (!item || !itemId) return;

    try {
      setIsSaving(true);
      setError(null);

      const updateData: UpdateItemRequest = {
        is_published: isPublished,
      };

      const response = await updateItem(helpCenterId, itemId, updateData);
      
      if (!response.ok) {
        throw new Error(response.error || 'Failed to update item');
      }

      if (response.item) {
        setItem(response.item);
      }

      toast.success('Guide item updated successfully!');
      onSuccess?.();
      onClose();
      
    } catch (err) {
      console.error('Error saving:', err);
      setError(err instanceof Error ? err.message : 'Failed to save');
      toast.error('Failed to save changes');
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditGuide = () => {
    const guideToUse = guide || item?.guide;
    if (guideToUse?.id) {
      window.open(`${BASE_PATH}/guides/${guideToUse.internalId}/edit`.replace(/\/+/g, '/') || '/', '_blank');
    }
  };

  const handleViewInPortal = () => {
    const guideToUse = guide || item?.guide;
    if (guideToUse) {
      const url = getGuidePublicUrl(guideToUse as Guide);
      window.open(url, '_blank');
    }
  };

  const handleCopyLink = () => {
    const guideToUse = guide || item?.guide;
    if (guideToUse) {
      const url = getGuidePublicUrl(guideToUse as Guide);
      navigator.clipboard.writeText(url);
      toast.success('Link copied to clipboard');
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Guide Details</DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : error && !item ? (
          <div className="py-6">
            <div className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-4 w-4" />
              <span>{error}</span>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Guide Information */}
            <div>
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold">
                    {guide?.title || item?.guide?.title || 'Loading guide...'}
                  </h3>
                  {(guide?.description || item?.guide?.description) && (
                    <p className="text-sm text-muted-foreground mt-1">
                      {guide?.description || item?.guide?.description}
                    </p>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleEditGuide}
                    disabled={!guide && !item?.guide}
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Edit Guide
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleViewInPortal}
                    disabled={!guide && !item?.guide}
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    View in Portal
                  </Button>
                </div>
              </div>

              {(() => {
                const guideToUse = guide || item?.guide;
                if (!guideToUse) return null;
                
                return (
                  <div className="space-y-4">
                    {/* Public URL */}
                    <div>
                      <Label className="text-sm font-medium">Public URL</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <code className="text-xs bg-muted px-2 py-1 rounded flex-1 truncate">
                          {getGuidePublicUrl(guideToUse as Guide)}
                        </code>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleCopyLink}
                        >
                          Copy
                        </Button>
                      </div>
                    </div>

                    <Separator />

                    {/* Guide Status Summary */}
                    <div>
                      <Label className="text-sm font-medium mb-2 block">Guide Status</Label>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="flex items-center gap-2">
                          {guideToUse.isPublished ? (
                            <>
                              <CheckCircle className="h-4 w-4 text-green-600" />
                              <span className="text-sm">Published</span>
                            </>
                          ) : (
                            <>
                              <XCircle className="h-4 w-4 text-muted-foreground" />
                              <span className="text-sm text-muted-foreground">Not Published</span>
                            </>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {guideToUse.publishSettings?.accessPasswordEnabled ? (
                            <>
                              <Lock className="h-4 w-4 text-yellow-600" />
                              <span className="text-sm">Password Protected</span>
                            </>
                          ) : (
                            <>
                              <Eye className="h-4 w-4 text-muted-foreground" />
                              <span className="text-sm text-muted-foreground">Public Access</span>
                            </>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {guideToUse.publishSettings?.asChecklist && (
                            <>
                              <CheckCircle className="h-4 w-4 text-blue-600" />
                              <span className="text-sm">Checklist Mode</span>
                            </>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {guideToUse.publishSettings?.showAuthor ? (
                            <>
                              <CheckCircle className="h-4 w-4 text-green-600" />
                              <span className="text-sm">Author Shown</span>
                            </>
                          ) : (
                            <>
                              <XCircle className="h-4 w-4 text-muted-foreground" />
                              <span className="text-sm text-muted-foreground">Author Hidden</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>

            <Separator />

            {/* Help Center Item Settings */}
            <div>
              <h3 className="text-sm font-semibold mb-2">Help Center Settings</h3>
              <p className="text-xs text-muted-foreground mb-4">
                Control how this guide appears in the help center
              </p>
              
              <div className="space-y-4">
                {/* Visibility Toggle */}
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="visibility" className="text-sm font-medium">
                      Visibility in Help Center
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      {isPublished 
                        ? 'This guide is visible in the help center'
                        : 'This guide is hidden in the help center (guide must also be published to be accessible)'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {isPublished ? (
                      <Eye className="h-4 w-4 text-green-600" />
                    ) : (
                      <EyeOff className="h-4 w-4 text-muted-foreground" />
                    )}
                    <Switch
                      id="visibility"
                      checked={isPublished}
                      onCheckedChange={setIsPublished}
                    />
                  </div>
                </div>

                {(() => {
                  const guideToUse = guide || item?.guide;
                  return !guideToUse?.isPublished && (
                    <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md p-3">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="h-4 w-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                        <div className="text-sm text-yellow-800 dark:text-yellow-200">
                          <p className="font-medium">Guide is not published</p>
                          <p className="mt-1">
                            The guide must be published in its settings before it can be accessed, even if it's visible in the help center.
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>

            {error && (
              <div className="flex items-center gap-2 text-destructive text-sm">
                <AlertCircle className="h-4 w-4" />
                <span>{error}</span>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={isSaving || loading}
          >
            {isSaving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Changes
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
